import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { AuthSessionService } from '../../services/auth-session.service';
import { ToastService } from '../../services/toast.service';
import { toFriendlyErrorMessage } from '../../utils/error-normalizer';

@Component({
  selector: 'app-login',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  mode: 'login' | 'forgot_request' | 'forgot_verify' | 'forgot_reset' | 'forgot_success' = 'login';
  email = '';
  password = '';
  otp = '';
  newPassword = '';
  confirmPassword = '';
  emailMasked = '';
  otpExpiresAt = '';
  resendAvailableAt = '';
  remainingResends: number | null = null;
  private resetToken = '';
  loading = false;
  sessionNotice = '';
  errorMessage = '';
  successMessage = '';

  private readonly authSession = inject(AuthSessionService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    if (this.authSession.isAuthenticated()) {
      void this.router.navigate(['/dashboard']);
      return;
    }

    const reason = this.route.snapshot.queryParamMap.get('reason');
    if (reason === 'session_expired') {
      this.sessionNotice = 'Your session expired. Please sign in again.';
      this.toast.warning(this.sessionNotice);
    }
  }

  submit(): void {
    if (this.mode !== 'login') {
      this.submitForgotPassword();
      return;
    }

    if (this.loading) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.authSession
       .login({
        email: this.email.trim().toLowerCase(),
        password: this.password,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.toast.success('Successfully signed in!');
          const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/dashboard';
          void this.router.navigateByUrl(returnUrl);
        },
        error: (error: unknown) => {
          this.errorMessage = toFriendlyErrorMessage(error, 'Login failed');
          this.toast.error(this.errorMessage);
          this.cdr.markForCheck();
        },
      });
  }

  startForgotPassword(): void {
    this.mode = 'forgot_request';
    this.errorMessage = '';
    this.successMessage = '';
  }

  backToLogin(message = ''): void {
    this.mode = 'login';
    this.otp = '';
    this.newPassword = '';
    this.confirmPassword = '';
    this.resetToken = '';
    this.errorMessage = '';
    this.successMessage = message;
  }

  resendForgotPasswordOtp(): void {
    if (this.loading || this.mode !== 'forgot_verify') {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.authSession
      .forgotPasswordResendOtp({ email: this.email.trim().toLowerCase() })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.otpExpiresAt = response.otpExpiresAt;
          this.resendAvailableAt = response.resendAvailableAt;
          this.remainingResends = response.remainingResends;
          this.toast.success('Verification code resent.');
        },
        error: (error: unknown) => {
          this.errorMessage = toFriendlyErrorMessage(error, 'Failed to resend OTP');
          this.toast.error(this.errorMessage);
        },
      });
  }

  private submitForgotPassword(): void {
    if (this.loading) {
      return;
    }

    if (this.mode === 'forgot_request') {
      this.requestForgotPasswordOtp();
      return;
    }

    if (this.mode === 'forgot_verify') {
      this.verifyForgotPasswordOtp();
      return;
    }

    if (this.mode === 'forgot_reset') {
      this.resetForgotPassword();
    }
  }

  private requestForgotPasswordOtp(): void {
    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authSession
      .forgotPasswordRequestOtp({ email: this.email.trim().toLowerCase() })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.mode = 'forgot_verify';
          this.emailMasked = response.emailMasked ?? this.email.trim().toLowerCase();
          this.otpExpiresAt = response.otpExpiresAt ?? '';
          this.resendAvailableAt = response.resendAvailableAt ?? '';
          this.remainingResends = 5;
          this.otp = '';
          this.toast.success('Verification code sent to your email.');
        },
        error: (error: unknown) => {
          this.errorMessage = toFriendlyErrorMessage(error, 'Failed to send OTP');
          this.toast.error(this.errorMessage);
        },
      });
  }

  private verifyForgotPasswordOtp(): void {
    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authSession
      .forgotPasswordVerifyOtp({
        email: this.email.trim().toLowerCase(),
        otp: this.otp.trim(),
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.resetToken = response.resetToken;
          this.mode = 'forgot_reset';
          this.newPassword = '';
          this.confirmPassword = '';
          this.toast.success('Verification successful.');
        },
        error: (error: unknown) => {
          this.errorMessage = toFriendlyErrorMessage(error, 'OTP verification failed');
          this.toast.error(this.errorMessage);
        },
      });
  }

  private resetForgotPassword(): void {
    if (this.newPassword !== this.confirmPassword) {
      this.errorMessage = 'Passwords do not match';
      this.toast.error(this.errorMessage);
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authSession
      .forgotPasswordReset({
        email: this.email.trim().toLowerCase(),
        resetToken: this.resetToken,
        newPassword: this.newPassword,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.mode = 'forgot_success';
          this.toast.success('Password reset successful.');
          this.backToLogin('Password reset successful. Please sign in with your new password.');
        },
        error: (error: unknown) => {
          this.errorMessage = toFriendlyErrorMessage(error, 'Failed to reset password');
          this.toast.error(this.errorMessage);
        },
      });
  }
}
